import React, { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { format, subMonths } from "date-fns";
import { FileText, FileDown, Mail, AlertCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, SectionHeader, EmptyState } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtCurrency, fmtNumber, fmtPct, computeDelta } from "@/lib/format";

export default function Reports() {
  const { selected, isAll, currentClient } = useClient();
  const { user } = useAuth();
  const single = !isAll && selected;
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const d = subMonths(new Date(), i);
    return { key: format(d, "yyyy-MM"), label: format(d, "MMMM yyyy") };
  }), []);
  const [month, setMonth] = useState(months[0].key);

  const { data, isLoading } = useQuery({
    queryKey: ["monthly", selected, month],
    queryFn: async () => (await api.get(`/clients/${selected}/reports/monthly`, { params: { month } })).data,
    enabled: !!single,
  });
  const { data: sales = [] } = useQuery({
    queryKey: ["salesreport", selected, month],
    queryFn: async () => {
      const y = Number(month.slice(0, 4)); const mo = Number(month.slice(5, 7));
      const start = new Date(Date.UTC(y, mo - 1, 1)).toISOString();
      const end = new Date(Date.UTC(y, mo, 1)).toISOString();
      return (await api.get(`/clients/${selected}/reports/salesperson`, { params: { start, end } })).data;
    },
    enabled: !!single,
  });

  const dl = async (fmt) => {
    try {
      const res = await api.get(`/clients/${selected}/reports/monthly/export`, { params: { month, format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `monthly-report-${month}.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (isAll) return <div><PageHeader title="Reports" /><EmptyState icon={AlertCircle} title="Select a client" subtitle="Choose a specific client to view its reports." /></div>;

  const m = data?.metrics || {};
  const p = data?.previous || {};

  const rows = [
    ["Signed Deal Value", "signed_deal_value", "currency"],
    ["Signed Deals", "signed_deals", "num"],
    ["Average Job Size", "avg_job_size", "currency", false],
    ["VSM Influenced Deals", "vsm_influenced_deals", "num"],
    ["VSM Influenced Value", "vsm_influenced_value", "currency"],
    ["Total Activities", "total_activities", "num"],
    ["Calls", "calls_count", "num"], ["Texts", "texts_count", "num"],
    ["Emails", "emails_count", "num"], ["Mailers", "mailers_count", "num"],
    ["New Leads", "new_leads", "num"], ["Moved to Prospect", "moved_to_prospect", "num"],
    ["Re-Engaged (VSM) — current", "re_engaged_count", "num"], ["Moved to Re-Engaged (VSM)", "moved_to_re_engaged", "num"],
    ["Moved to Deal", "moved_to_deal", "num"], ["Moved to Lost", "moved_to_lost", "num", false],
    ["Lead → Prospect %", "lead_to_prospect_rate", "pct"], ["Prospect → Deal %", "prospect_to_deal_rate", "pct"],
    ["Lead → Deal %", "lead_to_deal_rate", "pct"], ["Active Pipeline Value", "active_pipeline_value", "currency"],
    ["Referral Revenue", "referral_revenue", "currency"],
  ];
  const fmtV = (v, t) => t === "currency" ? fmtCurrency(v) : t === "pct" ? fmtPct(v) : fmtNumber(v);

  return (
    <div>
      <PageHeader title="Monthly Report" subtitle={currentClient?.business_name}>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-[180px]" data-testid="month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{months.map((mo) => <SelectItem key={mo.key} value={mo.key}>{mo.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={() => dl("csv")} data-testid="export-csv-btn"><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
        <Button variant="outline" onClick={() => dl("pdf")} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
        <EmailReportDialog clientId={selected} month={month} clientEmail={data?.client?.email} userEmail={user?.email} />
      </PageHeader>

      {isLoading ? <Spinner /> : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50"><TableHead>Metric</TableHead><TableHead className="text-right">This Month</TableHead><TableHead className="text-right">Previous</TableHead><TableHead className="text-right">Change</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(([label, key, t, higher = true]) => {
                  const d = computeDelta(m[key], p[key], higher);
                  const color = d.good === "good" ? "text-emerald-600" : d.good === "bad" ? "text-rose-600" : "text-slate-400";
                  return (
                    <TableRow key={key} data-testid={`report-row-${key}`}>
                      <TableCell className="font-medium text-slate-700">{label}</TableCell>
                      <TableCell className="text-right font-bold tabular-nums text-slate-900">{fmtV(m[key], t)}</TableCell>
                      <TableCell className="text-right tabular-nums text-slate-500">{fmtV(p[key], t)}</TableCell>
                      <TableCell className={`text-right text-sm font-semibold ${color}`}>{d.dir === "up" ? "↑" : d.dir === "down" ? "↓" : "—"} {d.pct.toFixed(1)}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <SectionHeader>Sales Team Performance (all-time)</SectionHeader>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50"><TableHead>Salesperson</TableHead><TableHead>Leads</TableHead><TableHead>Prospects</TableHead><TableHead>Re-Engaged</TableHead><TableHead>Deals</TableHead><TableHead>Won</TableHead><TableHead>Lost</TableHead><TableHead>Close Rate</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Avg Deal</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {sales.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8">No data</TableCell></TableRow> :
                  sales.map((s) => (
                    <TableRow key={s.salesperson} data-testid={`sales-row-${s.salesperson}`}>
                      <TableCell className="font-semibold text-slate-800">{s.salesperson}</TableCell>
                      <TableCell>{s.LEAD}</TableCell><TableCell>{s.PROSPECT}</TableCell><TableCell>{s.RE_ENGAGED}</TableCell><TableCell>{s.DEAL}</TableCell><TableCell>{s.WON}</TableCell><TableCell>{s.LOST}</TableCell>
                      <TableCell>{fmtPct(s.close_rate)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{fmtCurrency(s.revenue)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtCurrency(s.avg_deal_value)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            </div>
          </div>
          <div className="h-6" />
        </>
      )}
    </div>
  );
}

function EmailReportDialog({ clientId, month, clientEmail, userEmail }) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState("client");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/clients/${clientId}/reports/monthly/email`, { month, recipient })).data,
    onSuccess: (d) => { toast.success(`Report emailed to ${d.to}`); setOpen(false); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const target = recipient === "me" ? userEmail : clientEmail;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="email-report-btn"><Mail className="h-4 w-4 mr-1.5" /> Email</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Email Monthly Report</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Send to</Label>
            <Select value={recipient} onValueChange={setRecipient}>
              <SelectTrigger className="mt-1" data-testid="email-recipient-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client contact{clientEmail ? ` (${clientEmail})` : ""}</SelectItem>
                <SelectItem value="me">Me{userEmail ? ` (${userEmail})` : ""}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-slate-500">A formatted VSM performance summary for this month will be emailed{target ? ` to ${target}` : ""}.</p>
        </div>
        <DialogFooter>
          <Button onClick={() => mut.mutate()} disabled={!target || mut.isPending} data-testid="send-report-email-btn">
            {mut.isPending ? "Sending…" : "Send Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
